import { createContext, useContext } from 'react'

import type { HandedOffChart } from 'features/charts-v2/types/chart-handoff'
import type { ChartPageContext } from 'features/charts-v2/types/chart-page-context'

export interface ChartPageRegistrationType {
  /**
   * Every panel on the page, which one is active, and the sync toggles — one
   * value, so the ids a write comes back naming cannot describe a different
   * page from the panels the agent read.
   */
  setChartPage: (chartPage: ChartPageContext | null) => void
  setIsOnChartsPage: (onChartsPage: boolean) => void
  /** Which chart the page just received via a "View in charts" handoff. */
  setHandedOffChart: (chart: HandedOffChart | null) => void
}

/**
 * The two setters the charts page pushes into, and nothing else.
 *
 * **A separate context because a stable value is the whole point.** Both are
 * `useState` setters, so this value never changes after mount — while the main
 * `AskYogiAnywhereContext` re-memoises whenever the widget opens, wiki mode
 * changes, or a benchmarking question is set. The charts page reads none of
 * those and subscribed to all of them: measured at one extra render of a
 * ~1500-line page per widget toggle, on every user rather than the pilot.
 *
 * React context has no selector, so splitting the value IS the selector. The
 * page reads this; everything that needs the widget's state keeps reading the
 * main one, which is unchanged.
 */
export const ChartPageRegistrationContext =
  createContext<ChartPageRegistrationType | null>(null)

export const useChartPageRegistration = (): ChartPageRegistrationType => {
  const context = useContext(ChartPageRegistrationContext)
  if (context === null) {
    throw new Error(
      'useChartPageRegistration must be used within an AskYogiAnywhereProvider',
    )
  }
  return context
}
